import { GIT_RELEASE_BRANCH, KLARNA_WIKI_BASE_URL } from '@/config'
import type { AbRule, StackStatus, TableDataType } from '@/types'
import { predefinedColor } from '@/utils/teamColors'
import type { ApiSchema, Policy, Rule, UserFunction } from '@klarna/cbrms-policy-rules-manager-client-ts'
import { startCase } from 'lodash'
import moment from 'moment'
import { getCurrentInstance } from 'vue'
import { apiSchemas } from './constants'
import { buildPolicyRepoUrl, buildRepoUrl, buildRuleRepoUrl, buildUserFunctionRepoUrl } from './repository'

export const formattedDate = (value: string) => {
  const date = moment.utc(value).local()
  const utcHour = date.utcOffset() / 60 // UTC offset returns in minutes to converting to hour(s)

  return `${date.format('DD/MM/YYYY, HH:mm')} (UTC${utcHour > 0 ? `+${utcHour}` : ''})`
}

export function useDirectStore() {
  const instance = getCurrentInstance()
  if (!instance) {
    throw new Error('Missing current instance — useDirectStore() must be called inside <script setup> or setup().')
  }
  return instance.proxy.$store.direct
}

export function useQuasar() {
  const instance = getCurrentInstance()
  if (!instance) {
    throw new Error('Missing current instance — useQuasar() must be called inside <script setup> or setup().')
  }
  return instance.proxy.$q
}

export const deepClone = (ob: any): any => JSON.parse(JSON.stringify(ob))

export const userFunctionStatusLabel = ({ deprecated }: { deprecated: boolean }) =>
  deprecated ? 'Deprecated' : 'In Use'

// NOTE: the backend does the branch naming for us, i.e. transforming username to `draft/${username}`
export const draftBranchName = (username: string) => username

export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea')
  el.value = str
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

export const forceReload = () => {
  window.location.replace(window.location.href)
}

export const alertUser = (message: string) => {
  window.alert(message)
}

/**
 * A map of API names to their OpenAPI spec URL on the Klarna Wiki
 *  -  e.g.: https://wiki.klarna.net/wiki/Personal_Details_Update_(API_Spec)
 */
const OPENAPI_URLS = Object.freeze(
  Object.fromEntries(
    apiSchemas.map(api => [api, `${KLARNA_WIKI_BASE_URL}/${startCase(api).replaceAll(' ', '_')}_(API_Spec)`])
  )
) as Readonly<Record<ApiSchema, string>>

export const getApiLink = (api: ApiSchema | undefined) => OPENAPI_URLS[api!]

const deployedStatuses: StackStatus[] = ['CREATE_COMPLETE', 'UPDATE_COMPLETE', 'UPDATE_COMPLETE_CLEANUP_IN_PROGRESS']

export const isDeployed = (status: StackStatus) => deployedStatuses.includes(status)

/**
 * Would convert e.g. `'   sn4kE   B0mb!!  {"FLY"} '` to `'sn4ke_b0mb_fly'`.
 */
export const normalizeToValidId = (str: string) =>
  str
    .toLowerCase()
    .replace(/[^\sa-z0-9_]/g, '')
    .trim()
    .replace(/\s+/g, '_')
/**
 * Would convert e.g. `'  sn4kE   B0mb!!  {"FLY"} '` to `'sn4ke-b0mb-fly'`.
 */
export const normalizeLabel = (str: string) =>
  str
    .trim()
    .toLowerCase()
    .replace(/\s/g, '-')
    .replace(/[^a-z0-9-]/g, '')

type RulesToMetadataProps = {
  type: 'rule'
  data: Rule[]
}

type UserfunctionsToMetadataProps = {
  type: 'function'
  data: UserFunction[]
}

type PoliciesToMetadataProps = {
  type: 'policy'
  data: Policy[]
}

export type MetadataProps = RulesToMetadataProps | UserfunctionsToMetadataProps | PoliciesToMetadataProps

const isRulesMetadata = (props: MetadataProps): props is RulesToMetadataProps => props.type === 'rule'
const isPoliciesMetadata = (props: MetadataProps): props is PoliciesToMetadataProps => props.type === 'policy'

type TableColumn = {
  name: string
  label: string
  field: string
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  style?: string
}

export type TableItem = {
  name: string
  path: string
  teamCode: string
  teamName: string
  teamColor: string
  latestEditor: string
  latestUpdate: string
  bitbucketLink: string
  type: TableDataType
  api?: string
  apiLink?: string
  isDeployed?: boolean
  isShadow?: boolean
  latestCommit?: string
  latestReleaseCommit?: string
  sourceCommit?: string
  currentCommit?: string
  usedCount: number
  importCount?: number
}

export type TableMetadata = {
  columns: TableColumn[]
  data: TableItem[]
}

export const convertToTableMetadata = (props: MetadataProps, isDrafts: boolean): TableMetadata => {
  if (isRulesMetadata(props)) return convertRulesToTableMetadata(props.data, isDrafts)

  if (isPoliciesMetadata(props)) return convertPoliciesToTableMetadata(props.data, isDrafts)

  return convertUserFunctionsToTableMetadata(props.data, isDrafts)
}

const convertRulesToTableMetadata = (rules: Rule[], isDrafts: boolean): TableMetadata => ({
  columns: [
    {
      name: 'name',
      label: 'Name',
      field: 'name',
      sortable: true,
      align: 'left',
      style: 'width:450px',
    },
    {
      ...(isDrafts
        ? {
            name: 'branchName',
            label: 'Branch name',
            field: 'branchName',
            sortable: true,
            align: 'left',
            style: 'width:200px',
          }
        : {
            name: 'latestEditor',
            label: 'Latest editor',
            field: 'latestEditor',
            sortable: true,
            align: 'left',
            style: 'width:150px',
          }),
    },

    {
      name: 'api',
      label: 'Api',
      field: 'api',
      sortable: true,
      align: 'left',
      style: 'width:200px',
    },
    {
      name: 'latestUpdate',
      label: 'Latest update',
      field: 'latestUpdate',
      sortable: true,
      align: 'left',
      style: 'width: 150px',
    },
    {
      name: 'usedCount',
      label: 'Used in policies count',
      field: 'usedCount',
      sortable: true,
      align: 'left',
      style: 'width: 130px',
    },
    {
      name: 'bitbucketLink',
      label: 'Repository',
      field: 'name',
      sortable: true,
      align: 'left',
      style: 'width: 70px',
    },
  ],
  data: rules.map(rule => ({
    name: rule.id,
    path: '/rules/:rule_id/:branch?'
      .replace(':rule_id', rule.id)
      .replace(':branch?', isDrafts ? rule.branch_name : 'release'),
    api: rule.api,
    branchName: rule.branch_name,
    apiLink: getApiLink(rule.api),
    teamCode: rule.team_code,
    teamColor: predefinedColor[rule.team_name],
    teamName: rule.team_name,
    latestEditor: rule.latest_editor && rule.latest_editor.split('@')[0],
    latestUpdate: rule.latest_update,
    usedCount: rule.policies_count,
    bitbucketLink: buildRuleRepoUrl(rule.id),
    type: 'rule',
    pullRequest: rule.pull_request,
  })),
})

const convertUserFunctionsToTableMetadata = (userFunctions: UserFunction[], isDrafts: boolean): TableMetadata => ({
  columns: [
    {
      name: 'name',
      label: 'Name',
      field: 'name',
      sortable: true,
      align: 'left',
      style: 'width:400px',
    },
    {
      ...(isDrafts
        ? {
            name: 'branchName',
            label: 'Branch name',
            field: 'branchName',
            sortable: true,
            align: 'left',
            style: 'width:200px',
          }
        : {
            name: 'latestEditor',
            label: 'Latest editor',
            field: 'latestEditor',
            sortable: true,
            align: 'left',
            style: 'width:150px',
          }),
    },
    {
      name: 'teamName',
      label: 'Responsible team',
      field: 'teamName',
      sortable: true,
      align: 'left',
      style: 'width:200px',
    },
    {
      name: 'latestUpdate',
      label: 'Latest update',
      field: 'latestUpdate',
      sortable: true,
      align: 'left',
      style: 'width:150px',
    },
    {
      name: 'usedCount',
      label: 'Used in rules count',
      field: 'usedCount',
      sortable: true,
      align: 'left',
      style: 'width: 100px',
    },
    {
      name: 'bitbucketLink',
      label: 'Repository',
      field: 'name',
      sortable: true,
      align: 'left',
      style: 'width:50px',
    },
  ],
  data: userFunctions.map(userFunction => ({
    name: userFunction.id,
    path: '/user_functions/:user_function_id/:branch?'
      .replace(':user_function_id', userFunction.id)
      .replace(':branch?', isDrafts ? userFunction.branch_name : 'release'),
    teamCode: userFunction.team_code,
    teamName: userFunction.team_name,
    branchName: userFunction.branch_name,
    teamColor: predefinedColor[userFunction.team_name],
    latestEditor: userFunction.latest_editor && userFunction.latest_editor.split('@')[0],
    latestUpdate: userFunction.latest_update,
    bitbucketLink: buildUserFunctionRepoUrl(userFunction.id),
    type: 'function',
    usedCount: userFunction.rules_count,
    importCount: userFunction.import_count,
    pullRequest: userFunction.pull_request,
  })),
})

const convertPoliciesToTableMetadata = (policies: Policy[], isDrafts: boolean): TableMetadata => ({
  columns: [
    {
      name: 'name',
      label: 'Name',
      field: 'name',
      sortable: true,
      align: 'left',
      style: 'width:470px',
    },
    {
      ...(isDrafts
        ? {
            name: 'branchName',
            label: 'Branch name',
            field: 'branchName',
            sortable: true,
            align: 'left',
            style: 'width:200px',
          }
        : {
            name: 'latestEditor',
            label: 'Latest editor',
            field: 'latestEditor',
            sortable: true,
            align: 'left',
            style: 'width:150px',
          }),
    },
    {
      name: 'api',
      label: 'Api',
      field: 'api',
      sortable: true,
      align: 'left',
      style: 'width:200px',
    },
    {
      name: 'teamName',
      label: 'Responsible team',
      field: 'teamName',
      sortable: true,
      align: 'left',
      style: 'width:200px',
    },
    {
      name: 'latestUpdate',
      label: 'Latest update',
      field: 'latestUpdate',
      sortable: true,
      align: 'left',
      style: 'width:150px',
    },
    {
      name: 'usedCount',
      label: 'Number of rules used',
      field: 'usedCount',
      sortable: true,
      align: 'left',
      style: 'width: 130px',
    },
    {
      name: 'bitbucketLink',
      label: 'Repository',
      field: 'name',
      sortable: true,
      align: 'left',
      style: 'width:50px',
    },
  ],
  data: policies.map(policy => {
    return {
      name: policy.id,
      path: '/policies/:policy_id/:branch?'
        .replace(':policy_id', policy.id)
        .replace(':branch?', isDrafts ? policy.branch_name : 'release'),
      api: policy.api,
      apiLink: getApiLink(policy.api),
      teamCode: policy.team_code,
      teamName: policy.team_name,
      branchName: policy.branch_name,
      teamColor: predefinedColor[policy.team_name],
      latestEditor: policy.latest_editor && policy.latest_editor.split('@')[0],
      latestUpdate: policy.latest_update,
      bitbucketLink: buildPolicyRepoUrl(policy.id),
      isDeployed: policy.has_deployment,
      isShadow: policy.has_deployment && policy.branch_name !== GIT_RELEASE_BRANCH,
      type: 'policy',
      sourceCommit: policy.source_commit,
      latestReleaseCommit: policy.latest_release_commit,
      usedCount: policy.rules_count,
      pullRequest: policy.pull_request,
    }
  }),
})

export const getBitbucketPath = (type: TableDataType, name: string) => {
  return buildRepoUrl(type, name)
}

export const convertAbTestingRules = (champion: AbRule, challengers: AbRule[]) => ({
  champion: {
    id: champion.id,
    selectionRate: champion.selection_rate,
  },
  challengers: challengers.map((rule: AbRule) => {
    return {
      id: rule.id,
      selectionRate: rule.selection_rate,
    }
  }),
})
